import { HasAccess } from "@permify/react-role";
import {
  Alert,
  Button,
  Col,
  Empty,
  Image,
  Popconfirm,
  Popover,
  Row,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
  message
} from "antd";
import { useBoxContext } from '../../../context/BoxContext';
import { callPatient } from '../../../services/general';
import showBlockedCallModal from '../../ExamPage/components/BlockedCallModal';
import showOccupiedPatientModal from '../../ExamPage/components/OccupiedPatientModal';
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { consultationService } from "../../../services/medicalConsultation";
import { deferredExamService } from "../../../services/deferredExam";
import countryRestrictions from '../../../utils/countryRestrictions.json';
import History from '../../components/History';
import { getAsset } from './../../../services/general';
import ClinicalRecordModal from "./ClinicalRecordModal";
import PrescriptionsModal from "./PrescriptionsModal";
import DeferredExamModal from "./DeferredExamModal";
import DeferredExamHistory from "./DeferredExamHistory";
import { getCookie } from './../../../services/cookies';
import evaluationTypes from "../../../utils/evaluationType.json";

const { format } = require("rut.js");

const Header = ({
  id,
  serviceId,
  serviceType,
  serviceDetail,
  patientData,
  disableForm,
  medicalConsultationDetailId,
  parentType,
  status,
  reevaluationState,
  countrySettings,
  countryCode,
  medicalFormType,
  generatedPrescriptions,
  fetchGeneratedPrescriptions,
  hasObservedConsultation,
  hasDeferredExamPrescription,
  refreshDeferredExamPrescription,
  isConsultationSigned,
}) => {
  const [loading, setLoading] = useState({ idCard: false, prescriptions: false });
  const [loadingPerRow, setLoadingPerRow] = useState({});
  const { hasBoxes } = useBoxContext();
  const [patientIdCard, setPatientIdCard] = useState();
  const [isModalVisible, setIsModalVisible] = useState({
    record: false,
    prescription: false,
    deferredExam: false,
  });
  const [prescriptionsPopoverVisible, setPrescriptionsPopoverVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const [editingPrescription, setEditingPrescription] = useState(null);
  const [deferredExamPrescriptions, setDeferredExamPrescriptions] = useState([]);
  const { t } = useTranslation();
  const historyParams = {
    data: { type: 'medical_consultation_details', id: medicalConsultationDetailId },
    types: ['medical_consultation_details']
  }

  const fetchPatientIdCard = useCallback(
    async (params) => {
      try {
        setLoading((loading) => {
          return { ...loading, idCard: true };
        });

        const response = await getAsset({ id: params.id, type: 'Patient', field: 'document' });

        setPatientIdCard(response.document_url);
      } catch (error) {
        setPatientIdCard()
      } finally {
        setLoading((loading) => {
          return { ...loading, idCard: false };
        });
      }
    },
    [id, patientData]
  );

  useEffect(() => {
    if (id) {
      fetchPatientIdCard({ id: patientData.id });
    }
  }, [fetchPatientIdCard]);

  const getAge = (value) => {
    return moment().diff(value, "years");
  };

  const returnPage = () => {
    if (!parentType) {
      localStorage.removeItem("repeatCommentForAll");
      navigate({
        pathname: location.state ? location.state.from : '/consulta-medica/buscar',
        search: location.state && location.state.search ? location.state.search : null
      });
    } else {
      navigate(-1);
    }
  };

  const showClinicalRecordModal = () => {
    setIsModalVisible((isModalVisible) => {
      return { ...isModalVisible, record: true };
    });
  };

  const showPrescriptionsModal = () => {
    setEditingPrescription(null);
    setIsModalVisible((isModalVisible) => {
      return { ...isModalVisible, prescription: true };
    });
  };

  const handleEditPrescription = async (prescription) => {
    try {
      setLoading((loading) => ({ ...loading, prescriptions: true }));
      const response = await consultationService.getMedicalPrescription(prescription.id);
      setEditingPrescription(response);
      setPrescriptionsPopoverVisible(false);
      setIsModalVisible((isModalVisible) => ({ ...isModalVisible, prescription: true }));
    } catch (error) {
      console.error(error);
      message.error({
        content: "Error al obtener el detalle de la interconsulta",
        style: {
          marginTop: "45vh"
        }
      });
    } finally {
      setLoading((loading) => ({ ...loading, prescriptions: false }));
    }
  };

  const handleDeletePrescription = async (prescription) => {
    try {
      setLoading((loading) => ({ ...loading, prescriptions: true }));
      await consultationService.deletePrescription(prescription.id);
      message.success("Interconsulta eliminada exitosamente");
      await showPopoverAndGetPrescriptions(medicalConsultationDetailId);
    } catch (error) {
      console.error(error);
      message.error({
        content: error?.message || "Error al eliminar la interconsulta"
      });
    } finally {
      setLoading((loading) => ({ ...loading, prescriptions: false }));
    }
  };

  const showPopoverAndGetPrescriptions = async (medicalConsultationDetailId) => {
    try {
      setLoading((loading) => ({ ...loading, prescriptions: true }));
      await fetchGeneratedPrescriptions(medicalConsultationDetailId);
      const promises = [];
      if (countrySettings.medicalConsultation.deferredExam?.[countryCode] && medicalConsultationDetailId) {
        promises.push(deferredExamService.listPrescriptions(medicalConsultationDetailId));
      }
      const [deferredResponse] = await Promise.all(promises);
      if (deferredResponse?.data) {
        setDeferredExamPrescriptions(deferredResponse.data);
      }
    } catch (error) {
      console.error(error);
      message.error({
        content: "Error al obtener las prescripciones",
        style: {
          marginTop: "45vh"
        }
      });
    } finally {
      setLoading((loading) => ({ ...loading, prescriptions: false }));
    }
  };


  let prescriptionsContent

  if (loading.prescriptions) {
    prescriptionsContent = <Spin className='cmt-spin' />
  } else if (generatedPrescriptions.length > 0) {
    prescriptionsContent = (
      <Row>
        <Col lg={24} xs={24}>
          <Alert
            className="alert-download-disclaimer mt-20 mb-20"
            type="info"
            message={t('medicalConsultations.header.alertDisclaimer')}
          />
        </Col>
        <Col lg={24}>
          <Table dataSource={generatedPrescriptions} pagination={false}>
            <Table.Column title="id" dataIndex="id" key="id" render={(value, record) => {
              return (
                <p>{record.id}</p>
              )
            }} />
            <Table.Column
              title={t('medicalConsultations.header.title')}
              dataIndex="file"
              key="file"
              render={(value, record) => {
                return (
                  <p
                    id="downloadPrescription"
                    onClick={() => downloadPrescription(record)}
                    style={{ cursor: 'pointer' }}
                  >
                    <strong>
                      {
                        countrySettings.prescriptionModal.documentName[countryCode] ?
                          `${record.document_file_name?.trim()}.pdf`
                          : `${record.id} - ${record.prescription_type}.pdf`
                      }
                    </strong>
                  </p>
                )
              }}
            />
            <Table.Column title="Tipo" dataIndex="label" key="label " render={(value, record) => {
              return (
                <p>{record.label}</p>
              )
            }} />
            <Table.Column title="Estado" dataIndex="state" key="state" render={(value, record) => {
              const showDetailedStates = countrySettings.prescriptionModal.showDetailedStates?.[countryCode];

              if (showDetailedStates && record.state) {
                const stateLabels = {
                  draft: { label: 'Borrador', color: 'orange' },
                  sent: { label: 'Enviado', color: 'green' },
                  pending_documents: { label: 'Pendiente de Documentos', color: 'blue' },
                  no_documents_required: { label: 'Sin Documentos Requeridos', color: 'cyan' },
                };

                if (stateLabels[record.state]) {
                  const stateInfo = stateLabels[record.state];
                  return <Tag color={stateInfo.color}>{stateInfo.label}</Tag>;
                }
              }

              return (
                <p>
                  {countryCode === 'PE' || record.is_valid ? <Tag color="green">ACTIVA</Tag> : <Tag color="red">CADUCADA</Tag>}
                </p>
              )
            }} />
            {countrySettings.prescriptionModal.showPrescriptionActions[countryCode] && (
              <Table.Column
                title="Acciones"
                key="actions"
                render={(value, record) => {
                  return (
                    <Row gutter={8}>
                      <Col>
                        <Button
                          disabled={disableForm}
                          onClick={() => handleEditPrescription(record)}
                        >
                          <i className="icon-system-edit" />
                        </Button>
                      </Col>
                      <Col>
                        <Popconfirm
                          title={record.state === 'sent'
                            ? 'Esta interconsulta ya fue firmada y enviada por correo. Al eliminarla, el correo enviado no podrá revertirse.'
                            : '¿Estás seguro de que deseas eliminar esta interconsulta?'}
                          onConfirm={() => handleDeletePrescription(record)}
                          okText="Sí"
                          cancelText="No"
                          disabled={disableForm}
                        >
                          <Button
                            disabled={disableForm}
                            danger
                          >
                            <i className="icon-system-delete" />
                          </Button>
                        </Popconfirm>
                      </Col>
                    </Row>
                  );
                }}
              />
            )}
          </Table>
        </Col>
      </Row>
    )
  } else {
    prescriptionsContent = (
      <Empty
        description={
          <span className="empty-style">{t('medicalConsultations.header.prescriptionDisclaimer')}</span>
        }
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    )
  }

  const hasDeferredExamTab = countrySettings.medicalConsultation.deferredExam?.[countryCode];

  const popoverContent = () => {
    if (hasDeferredExamTab) {
      return (
        <Tabs className="cmt-tabs" defaultActiveKey="prescriptions">
          <Tabs.TabPane tab={t('medicalConsultations.header.downloadPrescriptionPopover')} key="prescriptions">
            <Row justify="center" gutter={[0, 10]} className="prescriptions-content">
              {prescriptionsContent}
            </Row>
          </Tabs.TabPane>
          <Tabs.TabPane tab="Reevaluaciones" key="reevaluations">
            <DeferredExamHistory
              prescriptions={deferredExamPrescriptions}
              setPrescriptions={setDeferredExamPrescriptions}
              serviceDetail={serviceDetail}
              onPrescriptionCancelled={refreshDeferredExamPrescription}
            />
          </Tabs.TabPane>
        </Tabs>
      );
    }
    return (
      <Row justify="center" gutter={[0, 10]} className="prescriptions-content">
        {prescriptionsContent}
      </Row>
    );
  };

  const downloadPrescription = async (prescription) => {
    try {
      setLoading(loading => { return { ...loading, report: true } });
      const response = await getAsset({
        id: prescription.id,
        field: 'document',
        type: 'MedicalPrescription',
      });

      if (response['document_url'] && !response['missing']) {
        const file = await fetch(response.document_url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/pdf',
          },
        });
        downloadable(await file.blob(), countrySettings.prescriptionModal.documentName[countryCode] ?
          `${prescription.document_file_name?.trim()}.pdf`
          : `${prescription.id} - ${prescription.prescription_type}.pdf`)
      } else {
        message.error({
          content: 'Ha ocurrido un error al descargar el informe. Inténtalo de nuevo.',
          style: {
            marginTop: '35vh'
          }
        });
        setLoading(loading => { return { ...loading, report: false } });
      }
    } catch (error) {
      console.error(`Error interno: ${error}`);
      message.error({
        content: `Error interno al descargar Informe.`,
        style: {
          marginTop: '35vh'
        }
      });
      setLoading(loading => { return { ...loading, report: false } });
    }
  };

  const handleCallPatient = async (admissionPatientId, forceOccupiedCall = false, forceRecentCall = false) => {
    const box = getCookie('box_id');
    if (!box || typeof box !== 'string' || box.trim() === '') {
      message.warning(t('examPage.examTabs.callPatientBoxWarning'));
      return;
    }

    setLoadingPerRow(prev => ({ ...prev, [admissionPatientId]: true }));

    try {
      await callPatient({ admissionPatientId: Number(admissionPatientId), box, forceOccupiedCall, forceRecentCall });
      message.success(t('examPage.examTabs.callPatientSuccess'));
    } catch (error) {
      const errorMessage = typeof error === 'string' ? error : error?.message || error?.toString?.() || 'Error';

      if (errorMessage?.includes('PATIENT_OCCUPIED_IN_EXAM') && !forceOccupiedCall) {
        setLoadingPerRow((prev) => ({ ...prev, [admissionPatientId]: false }));
        showOccupiedPatientModal(errorMessage, () => handleCallPatient(admissionPatientId, true, false), t);
        return;
      }

      if (errorMessage?.includes('PATIENT_RECENTLY_CALLED') && !forceRecentCall) {
        setLoadingPerRow((prev) => ({ ...prev, [admissionPatientId]: false }));
        showBlockedCallModal(errorMessage, () => handleCallPatient(admissionPatientId, forceOccupiedCall, true), t);
        return;
      }

      message.error(errorMessage || t('examPage.examTabs.callPatientError'));
      console.error('Error callPatient', error);
    } finally {
      setLoadingPerRow(prev => ({ ...prev, [admissionPatientId]: false }));
    }
  };

  const downloadable = (blob, fileName) => {
    const url = window.URL.createObjectURL(
      new Blob([blob]),
    );
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      fileName,
    );
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  };

  let documentType
  if (patientData?.document_type?.toLowerCase() === 'passport') {
    documentType = 'Pasaporte'
  } else if (patientData?.document_type === 'ce') {
    documentType = 'Carnet de extranjería'
  } else {
    documentType = patientData?.document_type?.toUpperCase()
  }

  let idCardTemplate
  if (loading.idCard) {
    idCardTemplate = (
      <Row justify="center">
        <Spin className="cmt-spin custom-spinner" />
      </Row>
    )
  } else if (patientIdCard && !patientIdCard.includes('missing.png')) {
    idCardTemplate = (
      <div className="patient-avatar-wrapper">
        <Image
          src={patientIdCard}
          alt={`Documento de identidad del ${t('medicalConsultations.header.patientTitle').toLocaleLowerCase()}`}
        />
      </div>
    )
  } else {
    idCardTemplate = (
      <div className="patient-avatar-wrapper">
        <Image
          src={'data:image/png;base64,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'}
          alt="Documento de identidad del paciente"
        />
      </div>
    )
  }

  const headerFields = {
    full_name: {
      label: t('medicalConsultations.header.patientTitle'),
      value: patientData?.full_name
    },
    age: {
      label: t('medicalConsultations.header.ageTitle'),
      value: getAge(patientData?.birthdate)
    },
    birthdate: {
      label: 'Fecha de nacimiento',
      value: patientData?.birthdate ? moment(patientData.birthdate).format('DD/MM/YYYY') : null
    },
    document: {
      label: documentType,
      value: patientData?.document_type === "rut" ? format(patientData?.document_number) : patientData?.document_number
    },
    profession: {
      label: t('medicalConsultations.header.professionTitle'),
      value: patientData?.position
    },
    company: {
      label: t('medicalConsultations.header.business_name'),
      value: patientData?.business_name
    },
    reservation: {
      label: t('medicalConsultations.header.reservation_number'),
      value: patientData?.reservation_number
    },
    evaluation_type: {
      label: t('medicalConsultations.header.evaluationTypeTitle'),
      value: (patientData?.evaluation_type || patientData?.evaluation_type === 0) ? (evaluationTypes[countryCode]?.find(type => type.value.toString() === patientData?.evaluation_type.toString())?.text || patientData?.evaluation_type) : null
    },
    outsourcing: {
      label: t('medicalConsultations.header.outsourcingTitle'),
      value: patientData?.outsourcing_company
    },
    third_company: {
      label: t('medicalConsultations.header.thirdCompanyTitle'),
      value: patientData?.third_company
    },
    project: {
      label: t('medicalConsultations.header.projectTitle'),
      value: patientData?.project
    },
    external_id: {
      label: t('medicalConsultations.header.externalIdTitle'),
      value: patientData?.external_id
    },
    area: {
      label: t('medicalConsultations.header.areaTitle'),
      value: patientData?.area
    },
    educational_lvl: {
      label: t('medicalConsultations.header.educationalLvlTitle'),
      value: patientData?.educational_lvl
    },
    id_card: {
      isTemplate: true,
      content: (
        <div className="id-card-wrapper">
          {idCardTemplate}
        </div>
      )
    }
  };

  const headerLayouts = {
    DEFAULT: {
      top: [
        { lg: 3, fields: ['id_card'] },
        { lg: 7, fields: ['full_name'] },
        { lg: 5, fields: ['document'] },
        { lg: 3, fields: ['age'] },
        { lg: 6, fields: ['birthdate'] }
      ],
      bottom: [
        { lg: 12, fields: ['company'] },
        { lg: 6, fields: ['profession'] },
        { lg: 6, fields: ['reservation'] }
      ]
    },
    PE: {
      top: [
        { lg: 3, fields: ['id_card'] },
        { lg: 6, fields: ['full_name'] },
        { lg: 4, fields: ['document'] },
        { lg: 2, fields: ['age'] },
        { lg: 4, fields: ['birthdate'] },
        { lg: 5, fields: ['educational_lvl'] }
      ],
      bottom: [
        { lg: 10, fields: ['company', 'outsourcing', 'third_company'] },
        { lg: 7, fields: ['project', 'area', 'profession'] },
        { lg: 7, fields: ['reservation', 'evaluation_type', 'external_id'] }
      ]
    }
  };

  const layoutKey = countrySettings.medicalConsultation.useSpecificHeaderLayout[countryCode] === true ? countryCode : 'DEFAULT';
  const headerLayout = headerLayouts[layoutKey];

  const renderField = (key, lg, index) => {
    if (!key) return <div key={index} style={{ height: '36px' }} />;
    const field = headerFields[key];
    if (field.isTemplate) return <div key={index}>{field.content}</div>;
    return (
      <div key={index} className="header-field-item-stacked">
        <span className="header-field-label">{field.label}:</span>
        <strong className="header-field-value">
          {field.value || '-'}
        </strong>
      </div>
    );
  };

  return (
    <Row
      justify="center"
      className="medical-consultation-detail__header pt-20 pb-20"
      gutter={[0, 30]}
    >
      <Col lg={22} xs={22}>
        <Row>
          <Col>
            <Button
              onClick={returnPage}
              className="cmt-button primary non-outline"
              type="primary"
            >
              <i className="icon-system-chevron-left" />
              Volver
            </Button>
          </Col>
        </Row>
      </Col>
      <Col lg={19} xs={22}>
        {
          patientData && (
            <Row>
              <Col lg={24} xs={24}>
                {
                  parentType ?
                    <h1 className="text-center mb-10">Firma de examen médico</h1>
                    :
                    <h1 className="text-center mb-10">
                      {t('medicalConsultations.header.title')}
                    </h1>
                }
              </Col>
              <Col lg={24} xs={24}>
                <Row wrap={true} gutter={[20, 0]} align="middle">
                  {headerLayout.top.map((column, colIndex) => (
                    <Col lg={column.lg} xs={24} key={`top-${colIndex}`}>
                      {column.fields.map((fieldKey, fieldIndex) => renderField(fieldKey, column.lg, fieldIndex))}
                    </Col>
                  ))}
                </Row>
                
                <div className="header-divider" />
                
                <Row wrap={true} gutter={[20, 0]}>
                  {headerLayout.bottom.map((column, colIndex) => (
                    <Col lg={column.lg} xs={24} key={`bottom-${colIndex}`}>
                      {column.fields.map((fieldKey, fieldIndex) => renderField(fieldKey, column.lg, fieldIndex))}
                    </Col>
                  ))}
                </Row>
                {
                  !parentType &&
                  <Row className="mt-20" justify="space-between" gutter={[20, 20]}>
                    <Col>
                      <Row gutter={[20, 20]}>
                        <Col>
                          <HasAccess
                            roles={['super_usuario', 'equipo_psicologia', 'doctor', 'direccion_medica']}
                            permissions='view-clinical-record'
                          >
                            <Button
                              id="clinicalRecordModalButton"
                              onClick={showClinicalRecordModal}
                              className="cmt-button secondary"
                              type="primary"
                            >
                              {t('medicalConsultations.header.clinicalRecordButton')}
                            </Button>
                          </HasAccess>
                        </Col>
                        {
                          (countryCode && countryRestrictions?.[countryCode] && !countryRestrictions[countryCode].find(item => item === 'reevaluation')) &&
                          <Col>
                            <HasAccess
                              roles={['super_usuario', 'equipo_psicologia', 'doctor', 'direccion_medica']}
                              permissions='generate-prescription'
                            >
                              <Button
                                id="prescriptionModalButton"
                                onClick={showPrescriptionsModal}
                                className="cmt-button secondary outline"
                                type="primary"
                                disabled={!medicalConsultationDetailId || disableForm}
                              >
                                {t('medicalConsultations.header.generatePrescriptionButton')}
                              </Button>
                            </HasAccess>
                          </Col>
                        }
                        {
                          countrySettings.medicalConsultation.deferredExam?.[countryCode] &&
                          <Col>
                            <HasAccess
                              roles={['super_usuario', 'doctor', 'direccion_medica']}
                              permissions='generate-prescription'
                            >
                              <Button
                                id="deferredExamModalButton"
                                onClick={() => setIsModalVisible(prev => ({ ...prev, deferredExam: true }))}
                                className="cmt-button primary"
                                type="primary"
                                disabled={!hasObservedConsultation || disableForm}
                              >
                                {hasDeferredExamPrescription ? 'Revisar Reevaluación' : 'Generar Reevaluación'}
                              </Button>
                            </HasAccess>
                          </Col>
                        }
                        <Col>
                          <Tooltip
                            title="Descargar interconsultas y reevaluaciones"
                            placement="top"
                            overlayClassName="download-button-tooltip"
                          >
                            <Popover
                              trigger="click"
                              title={hasDeferredExamTab ? null : t('medicalConsultations.header.downloadPrescriptionPopover')}
                              overlayClassName="download-prescriptions-popover"
                              content={popoverContent}
                              placement="bottom"
                              visible={prescriptionsPopoverVisible}
                              onVisibleChange={(visible) => {
                                setPrescriptionsPopoverVisible(visible);
                                if (visible) {
                                  showPopoverAndGetPrescriptions(medicalConsultationDetailId);
                                }
                              }}
                            >
                              <Button
                                id="prescriptionsPopoverButton"
                                className="cmt-button secondary outline"
                                type="primary"
                                disabled={!medicalConsultationDetailId || disableForm}
                              >
                                <span className="icon-system-download" />
                              </Button>
                            </Popover>
                          </Tooltip>
                        </Col>
                      </Row>
                    </Col>
                    
                    <Col>
                      <Row gutter={[20, 20]} align="middle">
                        {
                          medicalConsultationDetailId &&
                          <Col>
                            <History
                              params={historyParams}
                            />
                          </Col>
                        }
                        <Col>
                          <HasAccess
                            roles={['super_usuario', 'equipo_medico', 'equipo_psicologia', 'doctor', 'branch_office_manager']}
                            permission="call-patient"
                          >
                            {hasBoxes && (
                              <Popover
                                content={!moment().isSame(patientData?.admission_date, 'day') ? t('examPage.examTabs.callPatientDisabledTooltip') : ''}
                                placement="top"
                              >
                                <Button
                                  loading={loadingPerRow[id]}
                                  disabled={!moment().isSame(patientData?.admission_date, 'day') || loadingPerRow[id]}
                                  onClick={() => handleCallPatient(id)}
                                  className="cmt-button secondary"
                                  type="primary"
                                  size="medium"
                                  block
                                >
                                  {t('examPage.buttons.callPatient')}
                                </Button>
                              </Popover>
                            )}
                          </HasAccess>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                }
              </Col>
              <ClinicalRecordModal
                id={id}
                isModalVisible={isModalVisible}
                setIsModalVisible={setIsModalVisible}
                patientFullName={patientData.full_name}
                countryCode={countryCode}
                countrySettings={countrySettings}
              />
              <PrescriptionsModal
                id={id}
                serviceId={serviceId}
                serviceType={serviceType}
                serviceDetail={serviceDetail}
                patientData={patientData}
                isModalVisible={isModalVisible}
                setIsModalVisible={setIsModalVisible}
                medicalConsultationDetailId={medicalConsultationDetailId}
                countryCode={countryCode}
                status={status}
                reevaluationState={reevaluationState}
                countrySettings={countrySettings}
                medicalFormType={medicalFormType}
                editingPrescription={editingPrescription}
                setEditingPrescription={setEditingPrescription}
                onSuccess={() => fetchGeneratedPrescriptions(medicalConsultationDetailId)}
                isConsultationSigned={isConsultationSigned}
              />
              {countrySettings.medicalConsultation.deferredExam?.[countryCode] && (
                <DeferredExamModal
                  medicalConsultationDetailId={medicalConsultationDetailId}
                  isModalVisible={isModalVisible}
                  setIsModalVisible={setIsModalVisible}
                  serviceDetail={serviceDetail}
                  patientData={patientData}
                  onPrescriptionSaved={refreshDeferredExamPrescription}
                />
              )}
            </Row>
          )
        }
      </Col>

    </Row>
  );
};

export default Header;